import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Result, Spin } from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import config from "../../Configurations";
import { useLocation, useParams } from "react-router-dom";
import SurveyWidgetPanel from "../SurveyManagement/SurveyWidgetPanel";
import { SURVEY_FLOW_MODE } from "../SurveyManagement/constants";
import {
  checkSurveyEmail,
  collectClientDeviceMeta,
  fetchPublicInstrument,
  recordSurveyVisit,
  requestCaptchaPass,
  submitPublicSurveyResponse
} from "../SurveyManagement/surveyService";
import { validateEmail } from "../../util/common";
import { resolveSurveyInterfaceLanguage } from "../SurveyManagement/surveyInterfaceLanguage";
import { findFirstInvalidQuestion } from "../SurveyManagement/surveyWidgetFlow";
import { useEmbedHeightSync } from "./useEmbedHeightSync";
import { useSurveySessionContext } from "./useSurveySessionContext";
import { useKioskReset } from "./useKioskReset";
import ConsentCheckbox, { StartConsentCheckbox } from "./ConsentCheckbox";
import styles from "./style.module.scss";

const emptyContact = () => ({
  firstName: "",
  lastName: "",
  email: ""
});

const getContactValidationErrors = (contact, required = true) => {
  const errors = {};
  if (!required) {
    const email = contact.email.trim();
    if (email && !validateEmail(email)) errors.email = "invalid";
    return errors;
  }
  if (!contact.firstName.trim()) errors.firstName = "required";
  if (!contact.lastName.trim()) errors.lastName = "required";
  const email = contact.email.trim();
  if (!email) errors.email = "required";
  else if (!validateEmail(email)) errors.email = "invalid";
  return errors;
};

const buildContactPayload = (contact, required = true) => {
  const firstName = contact.firstName.trim();
  const lastName = contact.lastName.trim();
  const email = contact.email.trim();
  if (!required && !firstName && !lastName && !email) return null;
  return {
    first_name: firstName,
    last_name: lastName,
    email
  };
};

// Refusals from POST /feedback/public/responses that the visitor can act on.
const SUBMIT_REFUSAL_MESSAGES = {
  captcha_required: "surveyManagement.public.captcha_required",
  captcha_failed: "surveyManagement.public.captcha_failed",
  captcha_expired: "surveyManagement.public.captcha_expired",
  rate_limited: "surveyManagement.public.rate_limited",
  consent_required: "surveyManagement.public.consent_required",
  start_consent_required: "surveyManagement.public.start_consent_required",
  already_responded: "surveyManagement.public.already_responded",
  invalid_answer: "surveyManagement.public.invalid_answer",
  // The survey owner's response quota is full, or the survey closed while the visitor was answering.
  quota_exceeded: "surveyManagement.public.quota_exceeded",
  closed: "surveyManagement.public.reason_closed"
};

// "Last day: Wednesday 4 November, 23:59" in the survey's own time zone and language.
export const formatDeadline = (close, language, t) => {
  if (!close?.close_at) return "";
  const moment = new Date(close.close_at);
  if (Number.isNaN(moment.getTime())) return "";
  const zone = close.timezone || "Europe/Istanbul";
  const format = (options) => {
    try {
      return new Intl.DateTimeFormat(language || undefined, { timeZone: zone, ...options }).format(moment);
    } catch (_) {
      return new Intl.DateTimeFormat(language || undefined, options).format(moment);
    }
  };
  return t("surveyManagement.public.deadline", {
    date: format({ weekday: "long", day: "numeric", month: "long" }),
    time: format({ hour: "2-digit", minute: "2-digit", hour12: false }),
  });
};

const PublicSurvey = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  // Kiosk mode: the survey link opened with its kiosk key (pivony-api survey_kiosk). The API checks
  // the key; only then does the landing return the kiosk timings.
  const kioskKey = new URLSearchParams(location.search).get("kiosk") || "";
  const { getSessionContext } = useSurveySessionContext(embed);

  const [loading, setLoading] = useState(true);
  const [langReady, setLangReady] = useState(true);
  const [unavailableReason, setUnavailableReason] = useState(null);
  const [instrument, setInstrument] = useState(null);
  const [resolvedLanguage, setResolvedLanguage] = useState(null);
  const [channelId, setChannelId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState(emptyContact);
  const [contactErrors, setContactErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  // The robot check comes first: ticking it gets a pass from the API, which the submit sends.
  // When the pass is refused (expired or used), the box appears again at submit.
  const [captchaPass, setCaptchaPass] = useState("");
  const [gateChecking, setGateChecking] = useState(false);
  const [needFreshTick, setNeedFreshTick] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [consentSettings, setConsentSettings] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentMissing, setConsentMissing] = useState(false);
  const [videoSettings, setVideoSettings] = useState(null);
  // "Ask for consent before starting": the questions appear only after the box is ticked and
  // Start is pressed, and the submit says which wording was agreed to and when.
  const [startConsent, setStartConsent] = useState(null);
  // Closing rules (pivony-api survey_close): the message once closed, the deadline line, and the
  // start token that lets someone who opened the survey before the deadline still submit.
  const [closedMessage, setClosedMessage] = useState("");
  const [closeInfo, setCloseInfo] = useState(null);
  const [startToken, setStartToken] = useState("");
  const [startConsentAt, setStartConsentAt] = useState("");
  const [started, setStarted] = useState(false);
  const [kiosk, setKiosk] = useState(null);
  // Bumped on every kiosk reset so the form (and its step) starts over for the next person.
  const [kioskRound, setKioskRound] = useState(0);
  const captchaRef = useRef(null);
  const gateRef = useRef(null);
  // The survey list's view and start counts (direct link only, not the website pop-up).
  const visitStartedRef = useRef(false);

  useEffect(() => {
    if (!embed) return undefined;
    document.documentElement.classList.add("public-survey-embed");
    document.body.classList.add("public-survey-embed");
    return () => {
      document.documentElement.classList.remove("public-survey-embed");
      document.body.classList.remove("public-survey-embed");
    };
  }, [embed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setUnavailableReason(null);
      try {
        const { ok, body } = await fetchPublicInstrument(slug, { embed, kioskKey });
        if (cancelled) return;
        if (!ok || !body?.available) {
          setClosedMessage(body?.closed_message || "");
          setUnavailableReason(body?.reason || "not_found");
          setInstrument(null);
          return;
        }
        setInstrument(body.instrument);
        if (!embed) recordSurveyVisit(slug);
        setChannelId(body.channel_id);
        setCaptchaRequired(body.captcha_required === true);
        setConsentSettings(
          body.consent?.required === true ? body.consent : null
        );
        setVideoSettings(body.video || null);
        setStartConsent(
          body.start_consent?.required === true ? body.start_consent : null
        );
        setStartToken(body.start_token || "");
        setCloseInfo(body.close || null);
        setKiosk(body.kiosk?.enabled ? body.kiosk : null);
        const interfaceLang = resolveSurveyInterfaceLanguage({
          multilingualEnabled:
            body.instrument?.config?.multilingual_enabled === true,
          apiLanguage: body.language,
          primaryLanguage: body.instrument?.config?.primary_language
        });
        setResolvedLanguage(interfaceLang);
      } catch (_) {
        if (!cancelled) setUnavailableReason("error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, embed, kioskKey]);

  useEffect(() => {
    if (!resolvedLanguage) {
      setLangReady(true);
      return;
    }
    setLangReady(false);
    i18n.changeLanguage(resolvedLanguage).finally(() => setLangReady(true));
  }, [resolvedLanguage, i18n]);

  const cfg = instrument?.config || {};
  const questions = cfg.questions || [];
  const requestContactDetails = cfg.request_contact_details === true;
  const contactDetailsRequired = cfg.contact_details_required === true;
  const flowMode = cfg.flow_mode || SURVEY_FLOW_MODE.STEPPED;
  const thankYouMessage = cfg.thank_you_message || "";

  useEmbedHeightSync(embed && !loading && !!instrument, [
    submitted,
    submitting,
    answers,
    contact,
    submitError,
    captchaToken,
    consentGiven,
    started,
    startConsentAt,
    captchaPass
  ]);

  const setAnswer = (questionId, value) => {
    if (!embed && !visitStartedRef.current) {
      visitStartedRef.current = true;
      recordSurveyVisit(slug, { started: true });
    }
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const setContactField = (field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
    setContactErrors(prevErrors => {
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      if (field === "email" && value.trim() && validateEmail(value.trim())) {
        delete nextErrors.email;
      }
      return nextErrors;
    });
  };

  const validateEmailField = () => {
    const email = contact.email.trim();
    if (!email) {
      setContactErrors(prev => {
        const next = { ...prev };
        delete next.email;
        return next;
      });
      return;
    }
    setContactErrors(prev => {
      const next = { ...prev };
      if (validateEmail(email)) delete next.email;
      else next.email = "invalid";
      return next;
    });
  };

  const validate = () => {
    if (requestContactDetails && contactDetailsRequired) {
      const errors = getContactValidationErrors(contact, true);
      if (Object.keys(errors).length > 0) {
        setContactErrors(errors);
        return false;
      }
    } else if (requestContactDetails) {
      const errors = getContactValidationErrors(contact, false);
      if (Object.keys(errors).length > 0) {
        setContactErrors(errors);
        return false;
      }
    }
    return !findFirstInvalidQuestion(questions, answers);
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (requestContactDetails && contactDetailsRequired) {
      const errors = getContactValidationErrors(contact, true);
      if (Object.keys(errors).length > 0) {
        setContactErrors(errors);
        setSubmitError(t("surveyManagement.public.contact_required"));
        return;
      }
    } else if (requestContactDetails) {
      const errors = getContactValidationErrors(contact, false);
      if (Object.keys(errors).length > 0) {
        setContactErrors(errors);
        setSubmitError(t("surveyManagement.public.contact_invalid_email"));
        return;
      }
    }
    if (!validate()) {
      setSubmitError(t("surveyManagement.public.required_fields"));
      return;
    }
    if (consentSettings && !consentGiven) {
      setConsentMissing(true);
      setSubmitError(t(SUBMIT_REFUSAL_MESSAGES.consent_required));
      return;
    }
    if (captchaRequired && needFreshTick && !captchaToken) {
      setSubmitError(t(SUBMIT_REFUSAL_MESSAGES.captcha_required));
      return;
    }
    setSubmitting(true);
    try {
      const responseId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `rsp_${Date.now()}`;
      const payload = {
        slug,
        channel_id: channelId,
        embed,
        response_id: responseId,
        client_device: collectClientDeviceMeta(),
        session_context: getSessionContext(),
        answers: questions.map(q => ({
          question_id: q.id,
          type: q.type,
          value: answers[q.id] ?? null
        }))
      };
      if (requestContactDetails) {
        const contactPayload = buildContactPayload(
          contact,
          contactDetailsRequired
        );
        if (contactPayload) payload.contact = contactPayload;
      }
      if (consentSettings) payload.consent = consentGiven;
      if (startToken) payload.start_token = startToken;
      if (startConsent) {
        payload.start_consent = {
          given: true,
          version: startConsent.version,
          at: startConsentAt
        };
      }
      if (captchaRequired) {
        if (needFreshTick) payload.captcha_token = captchaToken;
        else payload.captcha_pass = captchaPass;
      }
      if (kiosk) payload.kiosk_key = kioskKey;
      const { ok, body } = await submitPublicSurveyResponse(payload);
      if (!ok || !body?.accepted) {
        const refusal = SUBMIT_REFUSAL_MESSAGES[body?.reason];
        if (
          ["captcha_required", "captcha_failed", "captcha_expired"].includes(
            body?.reason
          )
        ) {
          // A pass or token is good once: show the box here and tick it again.
          if (captchaRef.current) captchaRef.current.reset();
          setCaptchaToken("");
          setNeedFreshTick(true);
        }
        setSubmitError(t(refusal || "surveyManagement.widget.submit_error"));
        return;
      }
      setSubmitted(true);
      if (embed && window.parent) {
        window.parent.postMessage(
          { pivonyFeedback: "completed", responseId },
          "*"
        );
      }
    } catch (_) {
      setSubmitError(t("surveyManagement.widget.submit_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGateTick = async token => {
    if (!token) return;
    setGateChecking(true);
    setSubmitError("");
    try {
      const { ok, body } = await requestCaptchaPass(slug, token);
      if (ok && body?.passed) {
        if (body.pass) setCaptchaPass(body.pass);
        else setCaptchaRequired(false);
        return;
      }
      const refusal = SUBMIT_REFUSAL_MESSAGES[body?.reason];
      setSubmitError(t(refusal || "surveyManagement.public.captcha_failed"));
    } catch (_) {
      setSubmitError(t("surveyManagement.widget.submit_error"));
    } finally {
      setGateChecking(false);
    }
    if (gateRef.current) gateRef.current.reset();
  };

  const robotGate = captchaRequired && !captchaPass && !needFreshTick;
  const showGate = robotGate || (!!startConsent && !started);
  const canStart = !!startConsentAt && (!captchaRequired || !!captchaPass);

  const renderRobotCheck = () => (
    <ReCAPTCHA
      ref={gateRef}
      sitekey={config.RECAPTCHA_SITE_PUBLIC_KEY}
      hl={resolvedLanguage || i18n.language}
      onChange={handleGateTick}
    />
  );

  const renderGate = () => {
    if (!startConsent) {
      return (
        <>
          <p className={styles.gateTitle}>
            {t("surveyManagement.public.captcha_gate_title")}
          </p>
          <p className={styles.gateHint}>
            {gateChecking
              ? t("surveyManagement.public.captcha_checking")
              : t("surveyManagement.public.captcha_gate_hint")}
          </p>
          {renderRobotCheck()}
        </>
      );
    }
    return (
      <>
        <p className={styles.gateTitle}>
          {t("surveyManagement.public.captcha_gate_title")}
        </p>
        <p className={styles.gateHint}>
          {t(
            captchaRequired && !captchaPass
              ? "surveyManagement.public.start_gate_hint"
              : "surveyManagement.public.start_gate_hint_consent"
          )}
        </p>
        <StartConsentCheckbox
          checked={!!startConsentAt}
          onChange={checked =>
            setStartConsentAt(checked ? new Date().toISOString() : "")
          }
          text={startConsent.text}
          privacyUrl={startConsent.privacy_url}
        />
        {robotGate ? (
          <>
            {gateChecking ? (
              <p className={styles.gateHint}>
                {t("surveyManagement.public.captcha_checking")}
              </p>
            ) : null}
            {renderRobotCheck()}
          </>
        ) : captchaPass ? (
          <p className={styles.gateDone}>
            {t("surveyManagement.public.captcha_done")}
          </p>
        ) : null}
        <Button
          type="primary"
          size="large"
          className={styles.startButton}
          disabled={!canStart}
          onClick={() => {
            setSubmitError("");
            setStarted(true);
          }}
        >
          {t("surveyManagement.public.start_button")}
        </Button>
      </>
    );
  };

  const resetForNextPerson = () => {
    setAnswers({});
    setContact(emptyContact);
    setContactErrors({});
    setSubmitted(false);
    setSubmitError("");
    setConsentGiven(false);
    setConsentMissing(false);
    setStarted(false);
    setStartConsentAt("");
    setCaptchaToken("");
    setNeedFreshTick(false);
    setKioskRound(round => round + 1);
    window.scrollTo(0, 0);
  };

  const kioskInProgress =
    Object.keys(answers).length > 0 ||
    Object.values(contact || {}).some(v => typeof v === "string" && v.trim() !== "") ||
    consentGiven ||
    !!startConsentAt;
  useKioskReset({
    kiosk,
    submitted,
    inProgress: kioskInProgress,
    onReset: resetForNextPerson
  });

  useEffect(() => {
    if (!kiosk) return undefined;
    document.documentElement.classList.add("public-survey-kiosk");
    document.body.classList.add("public-survey-kiosk");
    return () => {
      document.documentElement.classList.remove("public-survey-kiosk");
      document.body.classList.remove("public-survey-kiosk");
    };
  }, [kiosk]);

  const shellClass = useMemo(
    () =>
      [styles.shell, embed ? styles.shellEmbed : "", kiosk ? styles.shellKiosk : ""]
        .filter(Boolean)
        .join(" "),
    [embed, kiosk]
  );

  if (loading || !langReady) {
    return (
      <div className={shellClass}>
        <Spin className={styles.centered} />
      </div>
    );
  }

  if (unavailableReason === "closed") {
    return (
      <div className={shellClass}>
        <Result
          status="info"
          title={t("surveyManagement.public.closed_title")}
          subTitle={closedMessage || t("surveyManagement.public.closed_default")}
        />
      </div>
    );
  }

  if (unavailableReason || !instrument) {
    return (
      <div className={shellClass}>
        <Result
          status="404"
          title={t("surveyManagement.public.unavailable_title")}
          subTitle={t(
            `surveyManagement.public.reason_${unavailableReason || "not_found"}`
          )}
        />
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <SurveyWidgetPanel
        key={kioskRound}
        title={instrument.title || ""}
        description={instrument.description || ""}
        questions={questions}
        widgetHeaderTitle={cfg.widget_header_title || ""}
        logoUrl={cfg.logo_url || ""}
        // No way off the page on a kiosk: the "Powered by" link would leave the survey.
        showBranding={!kiosk && cfg.show_branding !== false}
        requestContactDetails={requestContactDetails}
        contactDetailsRequired={contactDetailsRequired}
        primaryColor={cfg.primary_color || ""}
        flowMode={flowMode}
        thankYouMessage={thankYouMessage}
        interactive
        answers={answers}
        onAnswerChange={setAnswer}
        contact={contact}
        onContactFieldChange={setContactField}
        contactErrors={contactErrors}
        onContactEmailBlur={validateEmailField}
        submitting={submitting}
        submitted={submitted}
        submitError={submitError}
        onSubmit={handleSubmit}
        embed={embed}
        deadlineNote={formatDeadline(closeInfo, resolvedLanguage || i18n.language, t)}
        checkEmailTaken={
          cfg.one_response_per_email === true
            ? async email => {
                const { ok, body } = await checkSurveyEmail(slug, email, captchaPass, kiosk ? kioskKey : "");
                return ok && body?.checked ? body.already_responded === true : null;
              }
            : null
        }
        gateSlot={showGate ? renderGate() : null}
        submitDisabled={!!consentSettings && !consentGiven}
        videoUpload={
          videoSettings
            ? {
                slug,
                captchaPass,
                kioskKey: kiosk ? kioskKey : "",
                startConsent: startConsent
                  ? { given: true, version: startConsent.version, at: startConsentAt }
                  : null,
                startToken,
                enabled: videoSettings.enabled !== false,
                maxBytes: videoSettings.max_bytes,
                retentionDays: videoSettings.retention_days,
                company: videoSettings.company,
                contactEmail: cfg.video_contact_email || ""
              }
            : null
        }
        consentSlot={
          consentSettings ? (
            <ConsentCheckbox
              checked={consentGiven}
              onChange={checked => {
                setConsentGiven(checked);
                if (checked) {
                  setConsentMissing(false);
                  setSubmitError("");
                }
              }}
              company={consentSettings.company}
              language={resolvedLanguage || i18n.language}
              invalid={consentMissing}
            />
          ) : null
        }
        captchaSlot={
          captchaRequired && needFreshTick ? (
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={config.RECAPTCHA_SITE_PUBLIC_KEY}
              hl={resolvedLanguage || i18n.language}
              onChange={token => {
                setCaptchaToken(token || "");
                if (token) setSubmitError("");
              }}
              onExpired={() => setCaptchaToken("")}
            />
          ) : null
        }
      />
      {kiosk && submitted ? (
        <p className={styles.kioskNote} role="status">
          {t("surveyManagement.public.kiosk_next", { seconds: kiosk.reset_seconds || 5 })}
        </p>
      ) : null}
    </div>
  );
};

export default PublicSurvey;
